import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalDataTrack } from 'twilio-video';
import { determineMediaState, MediaStates } from '../../commands/practitioner/enums/MediaStates';
import { PlaybackType } from '../../commands/practitioner/enums/PlaybackType';
import useHandleOnDisconnect from '../../components/VideoProvider/useHandleOnDisconnect/useHandleOnDisconnect';
import BackendService from '../../services/BackendService';
import useCurrentSessionState from '../useCurrentSessionState/useCurrentSessionState';
import usePractitionerControlsContext from '../usePractitionerControlsContext/usePractitionerControlsContext';
import useVideoContext from '../useVideoContext/useVideoContext';
import useAppContext from '../useAppContext/useAppContext';

const useClientCommands = () => {
  const { t } = useTranslation();
  const [isConnectedToCall, setIsConnectedToCall] = useState(false);
  const [connectedToken, setConnectedToken] = useState('');
  const [twilioToken, setTwilioToken] = useState<string>('');
  const { setProjectionType } = usePractitionerControlsContext();
  const { room, connect, localTracks } = useVideoContext();
  const { sessionId, treatmentId } = useAppContext();
  const localDataTrack = localTracks.find(track => track.kind === 'data') as LocalDataTrack;
  const [isConnecting, setIsConnecting] = useState(false);
  useHandleOnDisconnect(room, () => setIsConnectedToCall(false));
  const { token, generatedUUID, mediaType, mediaProjection } = useCurrentSessionState(1000);

  useEffect(() => {
    if (token) {
      setTwilioToken(token);
    }
  }, [token]);

  useEffect(() => {
    if (generatedUUID) {
      BackendService.patchReceivedProperty(generatedUUID);
    }
  }, [generatedUUID]);

  useEffect(() => {
    setProjectionType(mediaProjection);
  }, [mediaProjection, setProjectionType]);

  const ConnectToRoom = async (tokenValue: string) => {
    // the client page connects to the room by token
    try {
      await connect(tokenValue, localDataTrack);
    } catch (e) {
      console.error(e);
      alert(t('Could not connect to room'));
    }
  };

  useEffect(() => {
    if (connectedToken !== twilioToken && localDataTrack && !isConnecting) {
      setIsConnecting(true);
      ConnectToRoom(twilioToken)
        .then(() => {
          setConnectedToken(twilioToken);
          setIsConnectedToCall(true);
          try {
            BackendService.sendMediaStatusReport(sessionId, treatmentId, 'call://connected', 1, 1.0);
          } catch {}
          setIsConnecting(false);
        })
        .catch(() => {
          setIsConnecting(false);
        });
    }
  }, [ConnectToRoom, twilioToken, isConnectedToCall, localDataTrack]);

  return {
    twilioToken,
    localDataTrack,
    room,
    connect,
    isConnectedToCall,
  };
};

export default useClientCommands;
